import { Control, Controller, FieldErrors, FieldPath, FieldValues, get } from 'react-hook-form';

import { RadioButton } from 'primereact/radiobutton';
import { SelectItemOptionsType } from 'primereact/selectitem';
import { classNames } from 'primereact/utils';

export type FormRadioGroupProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  className?: string;
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  field: TName;
  label: string;
  disabled?: boolean;
  options: SelectItemOptionsType | undefined;
  optionLabel?: string;
  optionValue?: string;
};

const FormRadioGroup = <TFieldValues extends FieldValues>({ ...props }: FormRadioGroupProps<TFieldValues>): JSX.Element => {
  const { className, control, field, errors, label, disabled, options, optionLabel, optionValue } = props;

  const errorTemplate = () => {
    const error = get(errors, field);
    return error && <small className="p-error">{error?.message as string}</small>;
  };

  return (
    <div className={className ?? 'field'}>
      <Controller
        name={field}
        control={control}
        render={({ field, fieldState }) => (
          <>
            <label htmlFor={field.name} className={classNames('font-medium', { 'p-error': fieldState.error, disabled })}>
              {label}
            </label>
            <div className="flex">
              {options?.map((option, idx) => {
                return (
                  <div key={`option-${idx}`} className={classNames('flex align-items-center', { 'ml-2': idx !== 0 })}>
                    <RadioButton
                      id={field.name}
                      name={optionLabel ? option[optionLabel] : option['label']}
                      value={optionValue ? option[optionValue] : option['value']}
                      onChange={(e) => field.onChange(e.value)}
                      checked={field.value === (optionValue ? option[optionValue] : option['value'])}
                    />
                    <label htmlFor={field.name} className="ml-2">
                      {optionLabel ? option[optionLabel] : option['label']}
                    </label>
                  </div>
                );
              })}
            </div>
          </>
        )}
      />
      {errorTemplate()}
    </div>
  );
};

export default FormRadioGroup;
